import type {Link} from '@github-ui/swp-core/schemas/contentful/contentTypes/link'

import type {
  GenericConditionalCTAs,
  GenericContent,
  GenericCtaPersonalisationRule,
  GenericCTAGroup,
  GenericCTASet,
} from './contentTypes'

export type GenericCtaCondition = GenericCtaPersonalisationRule['fields']['audienceCondition']

export type ResolvedGenericCta = {
  condition: GenericCtaCondition | 'default'
  primary: Link
  secondary?: Link
}

type ResolveGenericContentCtaOptions = {
  conditions?: readonly GenericCtaCondition[]
  legacyFallback?: 'default' | 'first'
  legacyLinksAsCta?: boolean
}

const LEGACY_CONDITIONS: Partial<Record<GenericCtaCondition, string>> = {
  hasOrganization: 'Has organization',
  isLoggedIn: 'Logged in',
}

export function getGenericContentLink(content: GenericContent): Link | undefined {
  return content.fields.link ?? content.fields.links?.at(0)
}

export function resolveGenericContentCta(
  content: GenericContent,
  options: ResolveGenericContentCtaOptions = {},
): ResolvedGenericCta | undefined {
  const {conditions = [], legacyFallback = 'default', legacyLinksAsCta = false} = options
  const {cta, ctas, links} = content.fields

  if (isGenericCtaGroup(cta)) {
    return toResolvedCta(cta, 'default')
  }

  if (isGenericCtaSet(cta)) {
    const activeConditions = new Set(conditions)
    const matchingRule = cta.fields.ctaPersonalisation.find(rule => activeConditions.has(rule.fields.audienceCondition))

    if (matchingRule) {
      return toResolvedCta(matchingRule.fields.cta, matchingRule.fields.audienceCondition)
    }

    return toResolvedCta(cta.fields.cta, 'default')
  }

  if (isGenericConditionalCtas(cta)) {
    for (const condition of conditions) {
      const group = cta.fields[condition]

      if (group) {
        return toResolvedCta(group, condition)
      }
    }

    return toResolvedCta(cta.fields.default, 'default')
  }

  for (const condition of conditions) {
    const legacyCondition = LEGACY_CONDITIONS[condition]
    const group = legacyCondition ? ctas?.find(candidate => candidate.fields.condition === legacyCondition) : undefined

    if (group) {
      return toResolvedCta(group, condition)
    }
  }

  const legacyGroup =
    legacyFallback === 'first' ? ctas?.at(0) : ctas?.find(candidate => candidate.fields.condition === 'None')

  if (legacyGroup) {
    return toResolvedCta(legacyGroup, 'default')
  }

  if (!legacyLinksAsCta) {
    return undefined
  }

  const primary = links?.at(0)

  if (!primary) {
    return undefined
  }

  return {
    condition: 'default',
    primary,
    secondary: links?.at(1),
  }
}

function isGenericCtaGroup(cta: GenericContent['fields']['cta']): cta is GenericCTAGroup {
  return cta?.sys.contentType.sys.id === 'genericCtaGroup'
}

function isGenericCtaSet(cta: GenericContent['fields']['cta']): cta is GenericCTASet {
  return cta?.sys.contentType.sys.id === 'genericCtaSet'
}

function isGenericConditionalCtas(cta: GenericContent['fields']['cta']): cta is GenericConditionalCTAs {
  return cta?.sys.contentType.sys.id === 'genericConditionalCtas'
}

function toResolvedCta(group: GenericCTAGroup, condition: ResolvedGenericCta['condition']): ResolvedGenericCta {
  return {
    condition,
    primary: group.fields.primary,
    secondary: group.fields.secondary,
  }
}
